export const HTTP_STATUS = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;

export const BANNER_NAME = {
  main: "main",
  "main-popup": "main-popup",
  personal: "personal",
} as const;

export const COMMUNITY_CATEGORY = {
  REALIZATION: "REALIZATION",
  JUST_CHAT: "JUST_CHAT",
} as const;

export const PLATFORM = {
  IOS: "IOS",
  ANDROID: "ANDROID",
} as const;

// 홈 카드 리스트에 포함되는 카드 타입에 대한 상수
export const HOME_CARD_TYPES = {
  HOME_NOTICE: "HOME_NOTICE", // 공지사항
  HOME_CUMULATIVE: "HOME_CUMULATIVE", // 인사 + 누적 스윙수
  HOME_MARKETING_BANNER: "HOME_MARKETING_BANNER", // 이벤트 배너
  HOME_TODAY_GOAL_MAIN: "HOME_TODAY_GOAL_MAIN", // 오늘의 목표 메인(신규)
  HOME_PERSONAL_BANNER: "HOME_PERSONAL_BANNER", // 개인화 배너
  HOME_TODAY_GOAL: "HOME_TODAY_GOAL", // 오늘의 목표(구)
  HOME_COMMUNITY: "HOME_COMMUNITY", // 커뮤니티
  HOME_RECOMMENDED_LESSON_VIDEO: "HOME_RECOMMENDED_LESSON_VIDEO", // 추천 레슨 영상
  HOME_GOLF_VIDEOS: "HOME_GOLF_VIDEOS", // 골프 영상
  HOME_HOW_TO_USE: "HOME_HOW_TO_USE", // 사용법
  HOME_SOCIAL_CHANNEL: "HOME_SOCIAL_CHANNEL", // 소셜 채널
} as const;

export interface YoutubeChannelWithoutThumbnail {
  name: string;
  id: string;
}

export const FEATURE_TYPE = {
  ANALYSIS: "ANALYSIS",
  FOCUS_DRILL_POSTURE: "FOCUS_DRILL_POSTURE",
  FOCUS_DRILL_RHYTHM_TEMPO: "FOCUS_DRILL_RHYTHM_TEMPO",
} as const;

export type FeatureType = keyof typeof FEATURE_TYPE;

/*
<product>/<product-version> (<system-information>)

아래의 문자열에 일치하는 정규표현식을 사용하여 golffix 앱 여부를 판별합니다.

e.g.
Golffix/2.57.0 (Android 11) webview // "Golffix/2.57.0 (Android" 까지 체크합니다.
Golffix/1.28.1 (ios 17.2.1) inapp // "Golffix/1.28.1 (ios" 까지 체크합니다.

참고
- https://www.notion.so/moais/Commit-Repository-Branch-8fced104f1f941b88727961341503590#9ddc9db726e84a9eaab018f7e2375447

실제 사용 시에는 "Unsafe Regular Expression"로 인해 간소화한 아래의 정규식을 적용했습니다.
*/
export const GOLFFIX_LEGEX = /Golffix\/\d+\.\d+\.\d+ \((Android|ios)/;

// Golffix 앱 버전 체크 (세 자리 버전: major.minor.patch)
const GOLFFIX_APP_VERSION_REGEX = /Golffix\/(\d+\.\d+\.\d+)/;

/**
 * userAgent에서 앱 버전을 추출합니다.
 * @example "Golffix/3.4.0 (ios 17.2.1) inapp" -> "3.4.0"
 */
export const extractAppVersion = (userAgent: string): string | null => {
  const match = userAgent.match(GOLFFIX_APP_VERSION_REGEX);
  return match ? match[1] : null;
};

/**
 * 두 버전을 비교합니다.
 * @returns version1 >= version2이면 true
 */
export const isVersionGreaterOrEqual = (
  version1: string,
  version2: string
): boolean => {
  const v1Parts = version1.split(".").map(Number);
  const v2Parts = version2.split(".").map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i += 1) {
    const v1 = v1Parts.at(i) || 0;
    const v2 = v2Parts.at(i) || 0;
    if (v1 > v2) return true;
    if (v1 < v2) return false;
  }
  return true;
};

// ios, android 대소문자 구별 없이 판별하기 위해 대소문자 구별 없는 정규식을 사용합니다.
export const IOS_LEGEX = /ios/i;
export const IOS_EXTEND_LEGEX =
  /iPhone|iPad|iPod|ios|Mac OS|Macintosh|Darwin|iPhone Simulator|iPad Simulator/i;
export const ANDROID_LEGEX = /Android/i;

/**
 * 앱 버전이 최소 버전 이상인지 확인합니다.
 * iOS/Android 앱에서만 동작하며, 앱이 아니거나 버전을 추출할 수 없으면 false를 반환합니다.
 * @param userAgent - User Agent 문자열
 * @param minVersion - 최소 버전 (예: "3.4.0")
 */
export const isAppVersionAtLeast = (
  userAgent: string,
  minVersion: string
): boolean => {
  const isIOSApp = IOS_LEGEX.test(userAgent);
  const isAndroidApp = ANDROID_LEGEX.test(userAgent);

  if (!isIOSApp && !isAndroidApp) return true;

  const appVersion = extractAppVersion(userAgent);
  if (!appVersion) return false;

  return isVersionGreaterOrEqual(appVersion, minVersion);
};

export const VIDEO_TYPE = {
  NONE: "NONE",
  LONG: "LONG",
  SHORT: "SHORT",
} as const;
